import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import ModalEditBudget from '../ModalEditBudget';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

const normalizeRoles = (roles) => {
  if (Array.isArray(roles)) {
    return roles
      .flatMap((role) => {
        if (Array.isArray(role)) return role;
        if (role && typeof role === 'object') {
          return role.nome || role.name || role.title || '';
        }
        return role;
      })
      .filter(Boolean)
      .map((role) => String(role).trim())
      .filter(Boolean);
  }

  if (typeof roles === 'string') {
    const trimmed = roles.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeRoles(parsed);
      }
    } catch (error) {
      // ignore parsing errors and fallback to comma split below
    }

    return trimmed
      .replace(/^[[{]+|[\]\\}]+$/g, '')
      .split(',')
      .map((role) => role.trim().replace(/['"]/g, ''))
      .filter(Boolean);
  }

  return [];
};

// const normalizeProjectName = (nome) => {
//   if (!nome) return "";
//   const parts = nome.split(" - ");
//   if (parts.length >= 2) {
//     return parts[1].trim();
//   }
//   return nome.trim();
// };

export default function Orcamento() {
  const [projetos, setProjetos] = useState([]);
  const [projetoSelecionado, setProjetoSelecionado] = useState('');
  const [dados, setDados] = useState(
    {
      atividades: [],
      executantes: [],
      totalHoras: 0,
      totalOrcamento: 0,
    },
  );
  const [loading, setLoading] = useState(false);
  const [loadingProjetos, setLoadingProjetos] = useState(false);
  const [isEditBudgetOpen, setIsEditBudgetOpen] = useState(false);

  useEffect(() => {
    const carregarProjetos = async () => {
      setLoadingProjetos(true);
      try {
        const response = await api.get('/api/planejamentoProjetos');
        setProjetos(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar projetos:', error);
      } finally {
        setLoadingProjetos(false);
      }
    };

    carregarProjetos();
  }, []);

  const projetoId = useMemo(() => {
    if (!projetoSelecionado) return '';
    const projeto = projetos.find((item) => item.nome === projetoSelecionado);
    return projeto ? projeto.id : '';
  }, [projetoSelecionado, projetos]);

  const buscarOrcamento = useCallback(async () => {
    if (!projetoId) {
      setDados({
        atividades: [],
        executantes: [],
        totalHoras: 0,
        totalOrcamento: 0,
      });
      return;
    }

    setLoading(true);
    try {
      const [budgetAllRes, userStoriesRes, estimatedHoursRes] =
        await Promise.all([
          api.get('/budget').catch(() => ({ data: [] })),
          api.get(`/budget/userstories/${projetoId}`),
          api.get(`/estimatedHours/${projetoId}`),
        ]);

      const userStories = userStoriesRes.data || [];
      const budgetGeral = budgetAllRes.data || [];
      const horasPorColaborador = estimatedHoursRes.data || [];

      const valorHoraPorColaborador = {};
      budgetGeral.forEach((item) => {
        const id = Number(item.id_colaborador);
        if (!Number.isNaN(id)) {
          valorHoraPorColaborador[id] = Number(item.valor_estimado || 0);
        }
      });

      const executantes = (horasPorColaborador || []).map((item) => {
        const idColaborador = Number(item.id_colaborador);
        const valorHoraColaborador = Number(
          valorHoraPorColaborador[idColaborador] || 0,
        );
        const horasEstimadas = Number(item.horas_estimadas || 0);

        return {
          id_colaborador: idColaborador,
          nome_colaborador: item.nome_colaborador,
          roles: normalizeRoles(item.roles),
          horas_estimadas: horasEstimadas,
          valor_estimado: horasEstimadas * valorHoraColaborador,
        };
      });

      const atividades = userStories.map((item) => {
        const horasPrevistas = Number(item.horas_previstas || 0);
        const idResponsavel = Number(item.id_responsavel);
        const valorHoraColaborador = Number(
          valorHoraPorColaborador[idResponsavel] || 0,
        );

        const gastoEstimado = horasPrevistas * valorHoraColaborador;

        return {
          id_historia_de_usuario: item.id_historia_de_usuario,
          nome_colaborador: item.nome_colaborador,
          titulo_hu: item.titulo_hu,
          horas_previstas: horasPrevistas,
          gasto_estimado: gastoEstimado,
        };
      });

      const totalHoras = atividades.reduce(
        (sum, item) => sum + Number(item.horas_previstas || 0),
        0,
      );

      const totalOrcamento = atividades.reduce(
        (sum, item) => sum + Number(item.gasto_estimado || 0),
        0,
      );

      setDados({ atividades, executantes, totalHoras, totalOrcamento });
    } catch (error) {
      console.error('Erro ao buscar orçamento do projeto:', error);
      setDados({
        atividades: [],
        executantes: [],
        totalHoras: 0,
        totalOrcamento: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [projetoId]);

  useEffect(() => {
    buscarOrcamento();
  }, [buscarOrcamento, projetoId]);

  return (
    <div
      style={{
        background: '#f3f4f6',
        minHeight: '100vh',
        padding: '32px 0 60px',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        <div
          style={{
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>Home</span>
          <span>›</span>
          <span>Gestão</span>
          <span>›</span>
          <strong style={{ color: '#374151' }}>Orçamento</strong>
        </div>

        <h1
          style={{
            fontSize: '44px',
            margin: 0,
            fontWeight: 700,
            color: '#1f2937',
          }}
        >
          Orçamento
        </h1>
        <p style={{ margin: '8px 0 20px', color: '#4b5563', fontSize: '18px' }}>
          Horas previstas e custo estimado por tarefas
        </p>

        <div
          style={{
            borderTop: '1px dashed #d1d5db',
            marginBottom: '24px',
            paddingTop: '18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 240 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                Selecione projeto
              </label>

              <select
                value={projetoSelecionado}
                onChange={(event) => setProjetoSelecionado(event.target.value)}
                disabled={loadingProjetos}
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  height: '42px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: '#fff',
                  fontSize: '16px',
                  color: '#111827',
                }}
              >
                <option value="">
                  {loadingProjetos ? 'Carregando...' : 'Selecione um projeto'}
                </option>
                {projetos.map((projeto) => (
                  <option key={projeto.id} value={projeto.nome}>
                    {projeto.nome}
                  </option>
                ))}
              </select>
            </div>

            {projetoSelecionado && (
              <button
                type="button"
                onClick={() => setIsEditBudgetOpen(true)}
                style={{
                  background: '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: 200,
                }}
              >
                Ajustar valor por hora
              </button>
            )}
          </div>

          {!projetoSelecionado && (
            <div
              style={{ marginTop: '10px', color: '#6b7280', fontSize: '14px' }}
            >
              Selecione o projeto para ver os detalhes do orçamento.
            </div>
          )}
        </div>

        {projetoSelecionado && (
          <>
            <ModalEditBudget
              isOpen={isEditBudgetOpen}
              onClose={() => {
                setIsEditBudgetOpen(false);
                buscarOrcamento();
              }}
              onSaved={() => {
                setIsEditBudgetOpen(false);
                buscarOrcamento();
              }}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(210px, 1fr))',
                gap: '18px',
                marginBottom: '28px',
              }}
            >
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #dfe3e8',
                  borderRadius: '10px',
                  padding: '18px 16px',
                  minHeight: '88px',
                }}
              >
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Atividades
                </div>
                <div
                  style={{
                    fontSize: '26px',
                    fontWeight: 700,
                    color: '#111827',
                    marginTop: '8px',
                  }}
                >
                  {dados.atividades.length}
                </div>
              </div>

              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #dfe3e8',
                  borderRadius: '10px',
                  padding: '18px 16px',
                  minHeight: '88px',
                }}
              >
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Horas previstas
                </div>
                <div
                  style={{
                    fontSize: '26px',
                    fontWeight: 700,
                    color: '#111827',
                    marginTop: '8px',
                  }}
                >
                  {dados.totalHoras}
                </div>
              </div>

              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #dfe3e8',
                  borderRadius: '10px',
                  padding: '18px 16px',
                  minHeight: '88px',
                }}
              >
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Custo total estimado
                </div>
                <div
                  style={{
                    fontSize: '26px',
                    fontWeight: 700,
                    color: '#111827',
                    marginTop: '8px',
                  }}
                >
                  {currency.format(dados.totalOrcamento)}
                </div>
              </div>
            </div>

            <div
              style={{
                marginBottom: '28px',
                background: '#f8fafc',
                border: '1px solid #dfe3e8',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '12px 14px',
                  color: '#374151',
                  fontWeight: 700,
                  fontSize: '18px',
                }}
              >
                Custo estimado por executante
              </div>

              {loading ? (
                <div style={{ padding: '18px 14px', color: '#6b7280' }}>
                  Carregando...
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f3f4f6' }}>
                      <tr>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Executante
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Cargo/Função
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Horas estimadas
                        </th>
                        <th
                          style={{
                            textAlign: 'right',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Valor estimado
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.executantes.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              textAlign: 'center',
                              color: '#6b7280',
                              padding: '18px',
                            }}
                          >
                            Nenhum dado encontrado para este projeto.
                          </td>
                        </tr>
                      )}

                      {dados.executantes.map((item) => (
                        <tr
                          key={item.id_colaborador}
                          style={{ background: '#ffffff' }}
                        >
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              color: '#111827',
                            }}
                          >
                            {item.nome_colaborador}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              color: '#111827',
                            }}
                          >
                            {item.roles && item.roles.length > 0
                              ? item.roles.join(', ')
                              : '-'}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              color: '#111827',
                            }}
                          >
                            {item.horas_estimadas}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              textAlign: 'right',
                              color: '#111827',
                            }}
                          >
                            {currency.format(item.valor_estimado || 0)}
                          </td>
                        </tr>
                      ))}

                      {dados.executantes.length > 0 && (
                        <tr style={{ background: '#edf5ff' }}>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            Total
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                            }}
                          ></td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            {dados.totalHoras}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            {currency.format(dados.totalOrcamento)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #dfe3e8',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '12px 14px',
                  color: '#374151',
                  fontWeight: 700,
                  fontSize: '18px',
                }}
              >
                Custo estimado por tarefa
              </div>

              {loading ? (
                <div style={{ padding: '18px 14px', color: '#6b7280' }}>
                  Carregando...
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f3f4f6' }}>
                      <tr>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Atividade/Tarefa
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Executante
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Horas previstas
                        </th>
                        <th
                          style={{
                            textAlign: 'right',
                            padding: '12px 14px',
                            color: '#374151',
                            borderBottom: '1px solid #dfe3e8',
                          }}
                        >
                          Gasto estimado
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.atividades.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              textAlign: 'center',
                              color: '#6b7280',
                              padding: '18px',
                            }}
                          >
                            Nenhum dado encontrado para este projeto.
                          </td>
                        </tr>
                      )}

                      {dados.atividades.map((item) => (
                        <tr
                          key={item.id_historia_de_usuario}
                          style={{
                            background: '#ffffff',
                          }}
                        >
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              color: '#111827',
                            }}
                          >
                            {item.titulo_hu}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              color: '#111827',
                            }}
                          >
                            {item.nome_colaborador}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              color: '#111827',
                            }}
                          >
                            {item.horas_previstas}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderBottom: '1px solid #e5e7eb',
                              textAlign: 'right',
                              color: '#111827',
                            }}
                          >
                            {currency.format(item.gasto_estimado || 0)}
                          </td>
                        </tr>
                      ))}

                      {dados.atividades.length > 0 && (
                        <tr style={{ background: '#edf5ff' }}>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            Total
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                            }}
                          ></td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            {dados.totalHoras}
                          </td>
                          <td
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #dfe3e8',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            {currency.format(dados.totalOrcamento)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
